import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Phone, Mail, Clock, Shield, ArrowRight } from 'lucide-react';
import {
  useVisaRecommenderPreference,
  recommendationToBookCounselFields,
} from '@/hooks/useVisaRecommenderPreference';
import PrefillBanner from '@/components/common/PrefillBanner';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { usePageMeta } from '@/hooks/useSEO';
import { trackCounsellorBooking } from '@/services/analytics';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';
import { SecureLeadForm } from '@/components/secure-lead/SecureLeadForm';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookCounsellingPage = () => {
  usePageMeta(
    'Book Free Study Abroad Counselling | MEA Approved Counsellors',
    'Book a free 1-on-1 counselling session with MEA-approved Zafco Study Abroad counsellors. Personalised university shortlist, scholarship guidance & visa support for Germany, UK, Canada, Australia & Ireland.',
  );
  const [searchParams] = useSearchParams();
  const { flags } = useFeatureFlags();
  const lqe = useLeadQualityEngine();
  const [form, setForm] = useState({
    name: '', email: '', destination: '', intake: '',
    education: '', ielts: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadSource, setLeadSource] = useState('book-counselling-direct');
  const [eligibilityContext, setEligibilityContext] = useState(null);

  // Pull the latest "Best Country for Me" quiz answers from localStorage.
  // URL params take precedence — recommender only fills empty fields.
  const { pref: visaPref, clear: clearVisaPref } = useVisaRecommenderPreference();
  const [prefilled, setPrefilled] = useState(false);

  // Pre-fill from quiz / tool deep-links
  useEffect(() => {
    const country = searchParams.get('country') || '';
    const eligibility = searchParams.get('eligibility') || '';
    const qualification = searchParams.get('qualification') || '';
    const english = searchParams.get('english') || '';
    const source = searchParams.get('source') || '';

    const qualMap = { '12th': '12th/HSC', 'bachelors': "Bachelor's", 'masters': "Master's", 'diploma': 'Diploma' };
    const ieltsMap = {
      'ielts-7.0+': 'IELTS 7.0+', 'ielts-6.5-7.0': 'IELTS 6.5-7.0',
      'ielts-6.0-6.5': 'IELTS 6.0-6.5', 'ielts-5.5-6.0': 'IELTS 5.5-6.0',
      'none-': 'Not Taken', 'pte-': 'PTE/TOEFL', 'toefl-': 'PTE/TOEFL',
    };

    if (country || eligibility || qualification || english) {
      setForm(prev => ({
        ...prev,
        destination: country || prev.destination,
        education: qualMap[qualification] || prev.education,
        ielts: ieltsMap[english] || (english.startsWith('pte') || english.startsWith('toefl') ? 'PTE/TOEFL' : prev.ielts),
      }));
      setLeadSource(source || 'visa-eligibility-quiz');
      if (eligibility) setEligibilityContext({ country, eligibility });
    } else if (source) {
      setLeadSource(source);
    }
  }, [searchParams]);

  // Layer the recommender preference on top — only fills empty fields,
  // so URL params from above always win. Hides on `clear()` via storage event.
  useEffect(() => {
    const next = recommendationToBookCounselFields(visaPref);
    if (!next) {
      setPrefilled(false);
      return;
    }
    setForm(prev => {
      const updates = {};
      Object.keys(next).forEach((k) => {
        if (!prev[k]) updates[k] = next[k];
      });
      if (Object.keys(updates).length === 0) return prev;
      setPrefilled(true);
      return { ...prev, ...updates };
    });
  }, [visaPref]);

  const dismissPrefill = () => {
    setPrefilled(false);
    clearVisaPref();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: lqe.phone,
          ...guard.payloadMeta,
          service_type: 'Study Abroad - Counselling',
          country: form.destination || 'Not specified',
          qualification: form.education || '',
          experience: form.ielts || '',
          source: leadSource,
          message: `[Book Counselling — ${leadSource}] Destination: ${form.destination || 'N/A'}, Intake: ${form.intake || 'N/A'}, Education: ${form.education || 'N/A'}, IELTS: ${form.ielts || 'N/A'}${eligibilityContext ? `, Eligibility: ${eligibilityContext.eligibility.toUpperCase()}` : ''}. Notes: ${form.message || 'None'}`,
        }),
      });
      if (lqe.interpretResponse(res)) return;
      lqe.markSubmitSuccess();
      // Conversion: highest-intent signal (counsellor booking)
      trackCounsellorBooking(leadSource, {
        destination: form.destination || 'unspecified',
        intake: form.intake || '',
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sa-book-counselling">
      {/* Hero */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#C5A028] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Shield className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Free Service — No Hidden Charges</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Book Your <span className="text-[#C5A028]">Free Counselling</span> Session
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            Speak with our certified study abroad counsellors and get personalized
            guidance on universities, visa process, scholarships, and career pathways.
          </p>
          {eligibilityContext && (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#C5A028]/15 backdrop-blur-sm border border-[#C5A028]/30 rounded-full px-4 py-1.5" data-testid="sa-eligibility-context-banner">
              <CheckCircle className="w-3.5 h-3.5 text-[#C5A028]" />
              <span className="text-xs text-white/90 font-medium">
                Pre-filled from your Visa Eligibility result &mdash; {eligibilityContext.country || 'Profile'} ({eligibilityContext.eligibility.replace('_', ' ')})
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form Column */}
            <div className="lg:col-span-3">
              {flags.secure_lead_pipeline ? (
                <SecureLeadForm source={leadSource} />
              ) : submitted ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold font-syne text-[#003087] mb-2">
                    Thank You for Booking!
                  </h2>
                  <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto">
                    Our counsellor will contact you within 24 hours to schedule your session.
                    In the meantime, explore our study destinations.
                  </p>
                  <Link
                    to="/study-abroad/"
                    className="inline-flex items-center gap-2 text-[#003087] font-semibold text-sm"
                  >
                    Explore Study Destinations <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
                  <h2 className="text-xl font-bold font-syne text-[#003087] mb-1">
                    Fill in Your Details
                  </h2>
                  <p className="text-slate-500 text-sm mb-6">
                    All fields marked with * are required
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="sa-counselling-form">
                    {prefilled && (
                      <PrefillBanner
                        variant="light"
                        onDismiss={dismissPrefill}
                        message={<>We&apos;ve set <em>Preferred Destination</em> based on your quiz answers — feel free to change.</>}
                        testid="sa-counsel-prefill-banner"
                      />
                    )}
                    <input
                      type="text" placeholder="Full Name *" required
                      className="sa-input" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="sa-counsel-name"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                      <input
                        type="email" placeholder="Email Address *" required
                        className="sa-input" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="sa-counsel-email"
                      />
                      <LeadPhoneField engine={lqe} testId="sa-counsel-phone" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <select className="sa-input" value={form.destination}
                        onChange={(e) => setForm({ ...form, destination: e.target.value })}
                        data-testid="sa-counsel-destination"
                      >
                        <option value="">Preferred Destination</option>
                        <option value="Germany">Germany</option>
                        <option value="UK">United Kingdom</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Not Sure">Not Sure Yet</option>
                      </select>
                      <select className="sa-input" value={form.intake}
                        onChange={(e) => setForm({ ...form, intake: e.target.value })}
                        data-testid="sa-counsel-intake"
                      >
                        <option value="">Preferred Intake</option>
                        <option value="Fall 2026">Fall 2026</option>
                        <option value="Spring 2027">Spring 2027</option>
                        <option value="Fall 2027">Fall 2027</option>
                        <option value="Not Sure">Not Sure</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <select className="sa-input" value={form.education}
                        onChange={(e) => setForm({ ...form, education: e.target.value })}
                      >
                        <option value="">Highest Education</option>
                        <option value="12th/HSC">12th / HSC</option>
                        <option value="Bachelor's">Bachelor's Degree</option>
                        <option value="Master's">Master's Degree</option>
                        <option value="Diploma">Diploma</option>
                      </select>
                      <select className="sa-input" value={form.ielts}
                        onChange={(e) => setForm({ ...form, ielts: e.target.value })}
                      >
                        <option value="">IELTS / English Test</option>
                        <option value="Not Taken">Not Taken Yet</option>
                        <option value="IELTS 5.5-6.0">IELTS 5.5 - 6.0</option>
                        <option value="IELTS 6.0-6.5">IELTS 6.0 - 6.5</option>
                        <option value="IELTS 6.5-7.0">IELTS 6.5 - 7.0</option>
                        <option value="IELTS 7.0+">IELTS 7.0+</option>
                        <option value="PTE/TOEFL">PTE / TOEFL</option>
                      </select>
                    </div>
                    <textarea
                      placeholder="Any specific questions or preferences? (Optional)"
                      className="sa-input min-h-[100px] resize-none"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                    <PrivacyConsentNotice tone="light" testid="sa-counsel-privacy-consent" />
                    <button
                      type="submit" disabled={submitting}
                      className="w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-4 rounded-full transition-all sa-pulse-cta disabled:opacity-60"
                      data-testid="sa-counsel-submit"
                    >
                      {submitting ? 'Submitting...' : 'Book My Free Counselling Session'}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Info Column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="text-base font-bold font-syne text-[#003087] mb-4">
                  What Happens Next?
                </h3>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'We Receive Your Details', desc: 'Your information is reviewed by our team.' },
                    { step: '2', title: 'Counsellor Assigned', desc: 'A specialist counsellor for your chosen destination contacts you.' },
                    { step: '3', title: 'Free Session (30 min)', desc: 'Discuss your profile, university options, and career goals.' },
                    { step: '4', title: 'Personalized Roadmap', desc: 'Receive a step-by-step plan for your study abroad journey.' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#003087] flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
                        <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#003087] rounded-2xl p-6 text-white">
                <h3 className="text-base font-bold font-syne mb-4">Contact Us Directly</h3>
                <div className="space-y-3">
                  <a href="tel:+919990533327" className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#C5A028] transition-colors">
                    <Phone className="w-4 h-4 text-[#C5A028]" /> +91-9990533327
                  </a>
                  <a href="mailto:studyabroad@zafco.in" className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#C5A028] transition-colors">
                    <Mail className="w-4 h-4 text-[#C5A028]" /> studyabroad@zafco.in
                  </a>
                  <div className="flex items-center gap-3 text-sm text-slate-300">
                    <Clock className="w-4 h-4 text-[#C5A028]" /> Mon - Sat, 10 AM - 7 PM IST
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h3 className="text-sm font-bold font-syne text-[#003087] mb-3">
                  Why Students Trust ZAFCO
                </h3>
                <div className="space-y-2.5">
                  {[
                    'Government of India (MEA) Approved',
                    '15+ Years of Experience',
                    '98% Visa Success Rate',
                    '500+ Partner Universities',
                    'Free Counselling — No Hidden Charges',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#C5A028] shrink-0" />
                      <span className="text-xs text-slate-700">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookCounsellingPage;
